<template>
  <div>
    <UserCtas :hide-user-cta="hideUserCta"
              @face-checkin-cta="$emit('face-checkin-cta')"
              v-if="hasGalleries"/>

    <template v-if="canSeeMedia">
      <div class="container" v-if="paidGalleries && paidGalleries.length > 0 || unlockedGalleries && unlockedGalleries.length > 0">
         <FaceInferenceCta :checkin-id="checkin.id" class="mb-4"/>
      </div>
      <InfosMedia class="container" v-if="paidGalleries && paidGalleries.length > 0"/>
      <MediaTabs @select="onSelectTab"/>
      <div>
        <UnlockedGalleries v-show="currentTab===checkinTabs.BOUGHT"
                           :galleries="unlockedGalleries"
                           :gallery-type="galleryTypes.UNLOCKED"
                           v-if="unlockedGalleries && unlockedGalleries.length > 0"/>

        <PaidGalleries v-show="currentTab===checkinTabs.BUYABLE"
                       :galleries="paidGalleries"
                       :gallery-type="galleryTypes.PAID"x
                       v-if="paidGalleries && paidGalleries.length > 0"/>

        <FavoriteGalleries v-show="currentTab===checkinTabs.FAVORITE"
                           :galleries="favoriteGalleries"
                           :gallery-type="galleryTypes.FAVORITE"
                           v-if="favoriteGalleries && favoriteGalleries.length > 0"/>
      </div>
    </template>
    <ModalFaceInference :checkin-id="checkin.id" @face-inference-started="onFaceInferenceStarted"/>
  </div>
</template>
<script>
import {mapActions, mapGetters} from "vuex";
import UnlockedGalleries from "@/domain/galleries/UnlockedGalleries.vue";
import {checkinTabs, galleryTypes} from "@/domain/galleries/constants";
import PaidGalleries from "@/domain/galleries/PaidGalleries.vue";
import MediaTabs from "@/domain/galleries/components/MediaTabs.vue";
import UserCtas from "@/domain/galleries/components/UserCtas.vue";
import FavoriteGalleries from "@/domain/galleries/FavoriteGalleries.vue";
import MessageManager from '@/domain/core/utils/messageManager'
import mqttTopics from '@/domain/core/constants/mqttTopics'
import eventBusConstants from '@/domain/core/constants/eventBus'
import InfosMedia from "@/domain/checkin/components/mediaInfo/InfosMedia.vue";
import FaceInferenceCta from "@/components/FaceInferenceCta.vue";
import ModalFaceInference from "@/components/ModalFaceInference.vue";

export default {
  name: 'Galleries',
  emits: ['face-checkin-cta'],
  data() {
    return {
      currentTab: checkinTabs.BOUGHT
    }
  },
  components: {
    ModalFaceInference,
    FaceInferenceCta,
    InfosMedia,
    FavoriteGalleries,
    UserCtas,
    MediaTabs,
    PaidGalleries,
    UnlockedGalleries
  },
  props: {
    hideUserCta: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('checkinDetail', ['checkin', 'canSeeMedia']),
    ...mapGetters('galleries', [
      'galleries',
      'unlockedGalleries',
      'paidGalleries',
      'favoriteGalleries'
    ]),
    galleryTypes() {
      return galleryTypes
    },
    checkinTabs() {
      return checkinTabs
    },
    hasGalleries() {
      return Object.keys(this.galleries).length > 0
    }
  },
  async mounted() {
    this.resetGalleries()
    await this.load()

    // Subscribe to personal media notifications (private galleries)
    MessageManager.on(mqttTopics.checkin.new_media_delivered, (data) => {
      this.handleMqttNotification(data, mqttTopics.checkin.new_media_delivered)
    }, `galleries-personal-${this.checkin.picacode}`)

    // Listen for notification_ready events to show popup
    this.registerEvent(eventBusConstants.realtimeNotifications.notification_ready, this.handleNotificationReady)
  },
  methods: {
    ...mapActions('galleries', [
      'fetchGalleries',
      'resetGalleries',
      'checkNewMedia']),
    async load() {
      await this.fetchGalleries({
        checkin_id: this.checkin.id
      })
    },
    onSelectTab(tab) {
      this.currentTab = tab
      if (tab === checkinTabs.FAVORITE) {
        window.dispatchEvent(new Event('scroll'))
      }
    },

    async handleMqttNotification(data, topic) {
      try {
        const refreshTime = 15 // Default 15 seconds for galleries

        // Emit MQTT message via event bus to RealtimeNotificationManager
        await this.emitEvent(eventBusConstants.realtimeNotifications.mqtt_message_received, {
          galleryId: 'galleries', // Use a general ID for galleries
          refreshTime,
          notificationData: {
            type: 'personal_media',
            galleryId: 'galleries',
            galleryName: 'Galleries',
            data: data,
            topic: topic,
            displayType: 'galleries'
          }
        })

      } catch (error) {
        console.error('Error handling MQTT notification in galleries:', error)
      }
    },

    async handleNotificationReady(payload) {
      const {galleryId, notificationData} = payload

      // Only handle notifications for galleries
      if (galleryId !== 'galleries') {
        return
      }

      try {
        // Check for new media in galleries
        const checkResult = await this.checkNewMedia({checkin_id: this.checkin.id})

        if (checkResult && checkResult.hasNewMedia) {
          if (checkResult.shouldShowPopup) {
            // Show popup via global notification system
            await this.emitEvent(eventBusConstants.realtimeNotifications.show_popup, {
              galleryId: 'galleries',
              galleryName: 'Galleries',
              checkResult: checkResult
            })
          }
        }

      } catch (error) {
        console.error('Error handling notification_ready in galleries:', error)
      }
    },

    onFaceInferenceStarted() {
      // Refresh galleries after face inference is started
      setTimeout(() => {
        this.load()
      }, 2000)
    },
  },
  beforeDestroy() {
    this.resetGalleries()

    // Cleanup MQTT subscriptions
    if (this.checkin && this.checkin.picacode) {
      MessageManager.off(`galleries-personal-${this.checkin.picacode}`)
    }

    // Unregister event listeners
    this.unregisterEvent(eventBusConstants.realtimeNotifications.notification_ready, this.handleNotificationReady)
  }
}

</script>
